import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useThemeGreyBackgroundColor } from "hooks/useThemeGreyBackgroundColor";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";

import { ScoutChat, type ScoutConversationSettingsState } from "./Chat";
import { SCOUT_INFO_PANEL_WIDTH_PX, ScoutInfoPanel, useScoutInfoPanel } from "./ScoutInfoPanel";
import { ScoutToolbar } from "./ScoutToolbar";

const SCROLLBAR_GUTTER_WIDTH = "16px";
const SCOUT_TOOLBAR_HEIGHT = 7; // 56px ÷ 8 default spacing

type ConversationTitleState = {
  conversationId: string | undefined;
  title: string | undefined;
};

function resolveDisplayedConversationTitle(input: {
  conversationTitle: ConversationTitleState | undefined;
  conversationId: string | undefined;
  hasMessages: boolean;
}) {
  if (!input.conversationTitle) {
    return undefined;
  }

  if (input.conversationTitle.conversationId === input.conversationId) {
    return input.conversationTitle.title;
  }

  if (!input.conversationId && input.hasMessages) {
    return input.conversationTitle.title;
  }

  return undefined;
}

export function Scout() {
  const backgroundColor = useThemeGreyBackgroundColor();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const height = isMobile ? xsHeight : mdHeight;

  const { conversationId } = useParams();
  const scoutCanvasRef = useRef<HTMLDivElement | null>(null);
  const [hasMessages, setHasMessages] = useState(false);
  const [conversationTitle, setConversationTitle] = useState<ConversationTitleState | undefined>();
  const [conversationSettings, setConversationSettings] = useState<ScoutConversationSettingsState>({
    allowMutations: false,
    onAllowMutationsChange: () => undefined,
    settingsDisabled: false,
  });
  const handleHasMessagesChange = useCallback((nextHasMessages: boolean) => {
    setHasMessages(nextHasMessages);
  }, []);
  const handleConversationTitleChange = useCallback((nextTitle: ConversationTitleState) => {
    setConversationTitle(nextTitle);
  }, []);
  useBreadcrumb({ label: "Encamp Scout" });
  const displayedConversationTitle = resolveDisplayedConversationTitle({
    conversationTitle,
    conversationId,
    hasMessages,
  });
  const {
    open: isInfoPanelOpen,
    tab: infoPanelTab,
    setTab: setInfoPanelTab,
    isPersistentMode: isPersistentInfoPanelMode,
    togglePrivacyPanel,
    closePanel: closeInfoPanel,
  } = useScoutInfoPanel();

  useEffect(() => {
    setHasMessages(false);
  }, [conversationId]);

  return (
    <Stack
      ref={scoutCanvasRef}
      sx={{
        position: "relative",
        width: "100%",
        height: `calc(100vh - ${theme.spacing(height)})`,
        overflow: "hidden",
        backgroundColor,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            position: "relative",
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            ...(isPersistentInfoPanelMode &&
              isInfoPanelOpen && {
                marginRight: `${SCOUT_INFO_PANEL_WIDTH_PX}px`,
                transition: theme.transitions.create("margin", {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }),
          }}
        >
          <ScoutToolbarOverlay backgroundColor={backgroundColor}>
            <ScoutToolbar
              conversationTitle={displayedConversationTitle}
              showLogo={Boolean(conversationId) || hasMessages}
              showTitle={Boolean(conversationId) || hasMessages}
              getSearchDialogContainer={() => scoutCanvasRef.current}
              allowMutations={conversationSettings.allowMutations}
              onAllowMutationsChange={conversationSettings.onAllowMutationsChange}
              settingsDisabled={conversationSettings.settingsDisabled}
              onTogglePrivacyPanel={togglePrivacyPanel}
            />
          </ScoutToolbarOverlay>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              marginTop: 7,
            }}
          >
            <ScoutChat
              onConversationTitleChange={handleConversationTitleChange}
              onHasMessagesChange={handleHasMessagesChange}
              onConversationSettingsChange={setConversationSettings}
            />
          </Box>
        </Stack>

        <ScoutInfoPanel
          variant={isPersistentInfoPanelMode ? "persistent" : "temporary"}
          open={isInfoPanelOpen}
          tab={infoPanelTab}
          onTabChange={setInfoPanelTab}
          onClose={closeInfoPanel}
        />
      </Box>
    </Stack>
  );
}

function ScoutToolbarOverlay({
  backgroundColor,
  children,
}: {
  backgroundColor: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        height: SCOUT_TOOLBAR_HEIGHT,
        position: "absolute",
        top: 0,
        left: 0,
        right: SCROLLBAR_GUTTER_WIDTH,
        zIndex: 2,
        backgroundColor,
        pointerEvents: "none",
      }}
    >
      {children}
    </Box>
  );
}
